import { useQuery } from '@tanstack/react-query';

import {
  checkIfUserIsAnyProductManager,
  getIndustryManagers,
  getProductManagers,
  getProductOwners,
  getRoadmapOwners,
  getRoadmapResources,
  getRoles,
  getTechnologyManagers,
  getUsers,
  getUsersWithAvailableHours,
  getUsersWithRoles,
} from '../api';
import { type UserWithRolesDTO } from '../types';

export const USE_USER_KEY = 'useUsers';
export const USE_USER_WITH_AVAILABLE_HOURS_KEY = 'useUsersWithAvailableHours';
export const ROADMAP_STATUS_RESOURCE_KEY = 'roadmap-status-resource-key';
export const USE_USER_WITH_ROLES_KEY = 'useUsersWithRoles';
export const USE_PRODUCT_OWNER_KEY = 'useProductOwners';
export const USE_PRODUCT_MANAGER_KEY = 'useProductManagers';
export const USE_TECHNOLOGY_MANAGER_KEY = 'useTechnologyManagers';
export const USE_INDUSTRY_MANAGER_KEY = 'useIndustryManagers';
export const USE_ROADMAP_OWNERS_KEY = 'useRoadmapOwners';
export const USE_ROLES_KEY = 'useRoles';

const DEFAULT_USE_USERS_OPTIONS = {
  hasCostGroup: false,
  enabled: true,
};

export const useUsers = (options?: Partial<typeof DEFAULT_USE_USERS_OPTIONS>) => {
  const mergedOptions = { ...DEFAULT_USE_USERS_OPTIONS, ...options };

  return useQuery({
    queryKey: [USE_USER_KEY, mergedOptions.hasCostGroup],
    queryFn: async () => await getUsers(mergedOptions.hasCostGroup),
    staleTime: 1000 * 60 * 15,
    enabled: mergedOptions.enabled,
  });
};

export const useUsersWithAvailableHours = (year: number) =>
  useQuery({
    queryKey: [USE_USER_WITH_AVAILABLE_HOURS_KEY, year],
    queryFn: async () => await getUsersWithAvailableHours(year),
    staleTime: 1000 * 60 * 15,
  });

export const useRoadmapResources = (enabled = true) =>
  useQuery({
    queryKey: ['users', ROADMAP_STATUS_RESOURCE_KEY],
    queryFn: getRoadmapResources,
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled,
  });

export const useIsUserAnyProductManager = () =>
  useQuery({
    queryKey: ['users', 'is-user-any-product-manager'],
    queryFn: async () => await checkIfUserIsAnyProductManager(),
    staleTime: 1000 * 60 * 20,
  });

export const useUsersWithRoles = (includeDeactivated = false, initialData?: UserWithRolesDTO[]) =>
  useQuery({
    queryKey: ['users', USE_USER_WITH_ROLES_KEY, includeDeactivated],
    queryFn: async () => await getUsersWithRoles(includeDeactivated),
    staleTime: 1000 * 60 * 15,
    initialData,
  });

export const useProductOwners = () =>
  useQuery({
    queryKey: ['users', USE_PRODUCT_OWNER_KEY],
    queryFn: getProductOwners,
    staleTime: 1000 * 60 * 15,
  });

export const useProductManagers = () =>
  useQuery({
    queryKey: ['users', USE_PRODUCT_MANAGER_KEY],
    queryFn: getProductManagers,
    staleTime: 1000 * 60 * 15,
  });

export const useTechnologyManagers = () =>
  useQuery({
    queryKey: ['users', USE_TECHNOLOGY_MANAGER_KEY],
    queryFn: getTechnologyManagers,
    staleTime: 1000 * 60 * 15,
  });

export const useIndustryManagers = () =>
  useQuery({
    queryKey: ['users', USE_INDUSTRY_MANAGER_KEY],
    queryFn: getIndustryManagers,
    staleTime: 1000 * 60 * 15,
  });

export const useRoadmapOwners = () =>
  useQuery({
    queryKey: ['users', USE_ROADMAP_OWNERS_KEY],
    queryFn: getRoadmapOwners,
    staleTime: 1000 * 60 * 15,
  });

export function useRoles() {
  return useQuery({
    queryKey: ['users', USE_ROLES_KEY],
    queryFn: getRoles,
    staleTime: 1000 * 60 * 30,
  });
}
